import React from 'react';
import connectToStores from 'alt-utils/lib/connectToStores';
import moment from 'moment';
import './CoachingQuickReminders.styl';
import UpcomingReviewsStore from 'stores/coaching/UpcomingReviewsStore';
import UserAvatar from 'components/pure/utils/UserAvatar';
import CoachingReviewTypeStore, {typesAndCategories} from 'stores/coaching/CoachingReviewTypeStore';
import CoachingPlaylistActions from 'actions/coaching/CoachingPlaylistActions';
import GenericLink from 'components/GenericLink';
import Button from '@material-ui/core/Button';
import {navigateToCallsTab} from 'utils/navigateToCallsTab';
import {getQueryParam} from 'utils/getQueryParam';
import {RepProfileTab} from '@store/coaching-plans';
import {browserHistory} from 'react-router';
import AuthStore from 'stores/AuthStore';
import UserStore from 'stores/UserStore';
import {ensureReviewMetadata} from 'components/StatsPage2.utils';
import {reviewPath} from 'components/dashboard/reviewPath';

/**
 * Banner that shows the upcoming review (NB: must come from the upcoming reviews endpoint)
 * associated with the user's most-recently-created plan.
 */
@connectToStores
export default class CoachingQuickReminders extends React.PureComponent {
  static getStores() {
    return [UpcomingReviewsStore, UserStore];
  }

  static getPropsFromStores({location}) {
    const selectedPlanIdQuery = getQueryParam(location, 'selectedPlanId');
    const selectedPlanId =
      typeof selectedPlanIdQuery === 'string' ? parseInt(selectedPlanIdQuery, 10) : undefined;
    const {upcomingReviews} = UpcomingReviewsStore.getState();
    const [reviewTypesLoaded, reviewTypes] = typesAndCategories(CoachingReviewTypeStore);
    const relevantReview = selectedPlanId
      ? upcomingReviews.find(review => review.planId === selectedPlanId)
      : null;
    return {
      relevantReview,
      reviewTypes,
      reviewTypesLoaded,
    };
  }

  componentDidMount() {
    // This is meant to be included in the admin dash, which already loads upcoming agendas, so we
    // won't bother loading them here.
    // If this component is ever used elsewhere, probably need to add them.
    setTimeout(() => ensureReviewMetadata(), 0); // Avoid dispatch in dispatch.
  }

  render() {
    const {relevantReview, reviewTypes, reviewTypesLoaded} = this.props;
    if (!reviewTypesLoaded || !relevantReview) {
      return null;
    }
    return (
      <div className="coaching-quick-reminders">
        <div className="title">Meeting Is Scheduled</div>
        <RelevantReviewReminderRow review={relevantReview} reviewTypes={reviewTypes} />
      </div>
    );
  }
}

const RelevantReviewReminderRow = ({review, reviewTypes}) => {
  const relevantReviewType = reviewTypes.find(reviewType => reviewType.id === review.reviewTypeId);
  // Found review type AND it includes calls => show add calls stuff.
  const includesCalls = relevantReviewType && {relevantReviewType};
  const {todo} = review;
  const canOpenPlaylist = includesCalls && !todo.done;
  return (
    <div className="relevant-review">
      <UserAvatar user={review.rep} enableTooltip />
      <div className="relevant-review-text">
        You'll have your first meeting with {review.rep.firstName} {upcomingDate(review.scheduleAt)}
        . We'll remind you nearer the time. {canOpenPlaylist && addCallsText(review)}
      </div>
      <div className="relevant-review-cta">
        <div>{addPreviewButton(review)}</div>
        <div>{canOpenPlaylist && addCallsButton(review)}</div>
      </div>
    </div>
  );
};

function upcomingDate(date) {
  const now = moment();
  if (date.isSame(now, 'day')) {
    return 'today';
  }
  if (date.isSame(now.clone().add(1, 'day'), 'day')) {
    return 'tomorrow';
  }
  if (date.isSame(now, 'week')) {
    return `this ${date.format('dddd')}`;
  }
  if (date.isSame(now.clone().add(1, 'week'), 'week')) {
    return `next ${date.format('dddd')}`;
  }
  return `on ${date.format('dddd, MMMM Do [at] h:mm a')}`;
}

function addCallsText(review) {
  return (
    <span>
      If you like, you can{' '}
      <GenericLink onClick={() => openPlaylist(review)}>add calls now</GenericLink>.
    </span>
  );
}

function addCallsButton(review) {
  return (
    <Button variant="outlined" className="ev-button" onClick={() => openPlaylist(review)}>
      Add Calls
    </Button>
  );
}

function addPreviewButton(review) {
  return (
    <Button variant="contained" color="primary" onClick={() => previewMeeting(review)}>
      Preview Meeting
    </Button>
  );
}

function openPlaylist(review) {
  CoachingPlaylistActions.setAddCallsMode({
    planId: review.planId,
    recipient: review.rep,
  });
  navigateToCallsTab();
}

function previewMeeting(review) {
  browserHistory.push({
    pathname: reviewPath(AuthStore.getState().user),
    query: {
      tab: RepProfileTab.COACHING_PLANS,
      planId: review.planId,
      reviewId: review.id,
      repId: review.rep.id,
      teamId: review.rep.teamId,
      // not autostarted
    },
  });
}
